import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Icon, IconButton, Button, Badge, Popover, PopoverTrigger, PopoverContent,
  PopoverHeader, PopoverBody, Tooltip,
} from '@chakra-ui/react';
import { FiEdit3, FiTrash2 } from 'react-icons/fi';
import ModalAddSurveyResponse from 'components/Modal/ModalAddSurveyResponse';
import { listAllDrafts, deleteDraftByKey, DRAFTS_CHANGED_EVENT } from 'services/autoSaveService';

const timeAgo = (iso) => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} d`;
};

// Lista TODOS los borradores pendientes de este navegador (de distintos alumnos y
// formularios) y permite retomar cualquiera sin tener que entrar al perfil del alumno.
const DraftsMenu = () => {
  const [drafts, setDrafts] = useState([]);
  const [active, setActive] = useState(null); // borrador que se está retomando
  const [confirmKey, setConfirmKey] = useState(null);

  const refresh = useCallback(() => setDrafts(listAllDrafts()), []);

  useEffect(() => {
    refresh();
    window.addEventListener(DRAFTS_CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh); // cambios hechos desde otra pestaña
    return () => {
      window.removeEventListener(DRAFTS_CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  if (drafts.length === 0 && !active) return null;

  return (
    <>
      <Popover placement="bottom-end" isLazy onOpen={refresh}>
        <PopoverTrigger>
          <Box position="relative" display="inline-block">
            <Tooltip label="Borradores pendientes" placement="bottom">
              <IconButton aria-label="Borradores pendientes" icon={<Icon as={FiEdit3} boxSize="17px" />}
                variant="ghost" borderRadius="10px" size="sm" />
            </Tooltip>
            <Badge position="absolute" top="-4px" right="-4px" borderRadius="999px" bg="coral.500" color="white"
              fontSize="10px" minW="16px" textAlign="center" pointerEvents="none">
              {drafts.length}
            </Badge>
          </Box>
        </PopoverTrigger>
        <PopoverContent w={{ base: '92vw', md: '380px' }} borderRadius="14px" fontFamily="jakarta">
          <PopoverHeader fontWeight="800" fontSize="14px" borderBottom="1px solid" borderColor="cream.line">
            Borradores pendientes ({drafts.length})
          </PopoverHeader>
          <PopoverBody p={0} maxH="60vh" overflowY="auto">
            {drafts.map((draft) => {
              const c = draft.customerData || {};
              const studentName = [c.first_name, c.last_name].filter(Boolean).join(' ') || draft.userId;
              return (
                <Flex key={draft.key} align="center" gap="8px" px={3} py="10px" borderBottom="1px solid" borderColor="cream.line">
                  <Box flex={1} minW={0}>
                    <Text fontSize="13px" fontWeight="800" color="ink.900" noOfLines={1}>{studentName}</Text>
                    <Text fontSize="12px" color="ink.700" noOfLines={1}>{draft.surveyName}</Text>
                    <Text fontSize="11px" color="ink.400" fontWeight="600">
                      {draft.percentComplete}% completo · {timeAgo(draft.lastSavedAt)}
                    </Text>
                  </Box>
                  {confirmKey === draft.key ? (
                    <>
                      <Button size="xs" colorScheme="red" onClick={() => { deleteDraftByKey(draft.key); setConfirmKey(null); }}>
                        Descartar
                      </Button>
                      <Button size="xs" variant="ghost" onClick={() => setConfirmKey(null)}>No</Button>
                    </>
                  ) : (
                    <>
                      <Button size="xs" bg="coral.500" color="white" _hover={{ bg: 'coral.600' }} onClick={() => setActive(draft)}>
                        Retomar
                      </Button>
                      <IconButton aria-label="Descartar borrador" size="xs" variant="ghost"
                        icon={<Icon as={FiTrash2} />} onClick={() => setConfirmKey(draft.key)} />
                    </>
                  )}
                </Flex>
              );
            })}
          </PopoverBody>
        </PopoverContent>
      </Popover>

      {active && (
        <ModalAddSurveyResponse
          isOpen
          onClose={() => { setActive(null); refresh(); }}
          customer={active.customerData}
          preselectedFormId={active.surveyId}
          casoId={active.casoId}
          updateSurveyResponses={refresh}
        />
      )}
    </>
  );
};

export default DraftsMenu;
